import type { MouseEvent } from 'react'

import { cn } from '@/utils'
import {
  anyFilterOn,
  filtersAfterPicking,
  NO_FILTERS,
  yearIsShown,
} from '../mapView'
import { STRETCH_HINT, type YearPickerProps } from './yearPick'

const YEAR_BUTTON =
  'h-6 cursor-pointer whitespace-nowrap rounded px-2.5 text-xs tabular-nums transition-colors'

const YEAR_PICKED = 'bg-neutral-900 font-medium text-white'

const YEAR_AT_REST =
  'text-ink-subtle hover:bg-neutral-100 hover:text-foreground'

// One joined control, so the years read as a choice and not as text. Whatever is showing is
// filled: "All years" at rest, the picked years once the map is narrowed.
export const YearRow = ({ years, filters, onFilters }: YearPickerProps) => {
  const everything = !anyFilterOn(filters)
  const pick = (year: number) => (event: MouseEvent) =>
    onFilters(filtersAfterPicking(year, event.shiftKey, filters))
  const isPicked = (year: number | undefined) =>
    !everything && year !== undefined && yearIsShown(year, filters)

  return (
    <fieldset
      aria-label="Years shown on the map"
      title={STRETCH_HINT}
      className="flex min-w-0 shrink-0 items-center gap-0.5 overflow-x-auto rounded-md border bg-white p-0.5 scrollbar-hide"
    >
      <button
        type="button"
        aria-pressed={everything}
        onClick={() => onFilters(NO_FILTERS)}
        className={cn(YEAR_BUTTON, everything ? YEAR_PICKED : YEAR_AT_REST)}
      >
        All years
      </button>
      {years.map((year, index) => {
        const picked = isPicked(year)
        return (
          <button
            key={year}
            type="button"
            aria-pressed={picked}
            onClick={pick(year)}
            className={cn(
              YEAR_BUTTON,
              picked ? YEAR_PICKED : YEAR_AT_REST,
              // A picked run reads as one span of time, so its years join into one bar.
              picked && isPicked(years[index - 1]) && 'rounded-l-none',
              picked && isPicked(years[index + 1]) && 'rounded-r-none',
            )}
          >
            {year}
          </button>
        )
      })}
    </fieldset>
  )
}
